import type { DocumentCounts, Template } from '@/types/domain'

/**
 * Everything the Übersicht decides that a click would otherwise be needed to
 * find: which forms a tab shows, what a card's status line says, what the
 * review banner is allowed to claim, and how a timestamp reads.
 *
 * All of it is pure and lives here rather than in the screen, because each
 * one is a statement about the user's data that must be true — the banner in
 * particular, which the artboard writes as a field count nothing in the
 * gateway can produce.
 */

export type OverviewTab = 'recent' | 'all' | 'favorites'

export const OVERVIEW_TABS: { value: OverviewTab; label: string }[] = [
  { value: 'recent', label: 'Zuletzt' },
  { value: 'all', label: 'Alle' },
  { value: 'favorites', label: 'Favoriten' },
]

/** "Zuletzt" is the eight most recently changed, as in the Flutter app. */
export const RECENT_LIMIT = 8

export function filterTemplates(
  templates: Template[],
  { tab, query, favorites }: { tab: OverviewTab; query: string; favorites: ReadonlySet<string> },
): Template[] {
  const q = query.trim().toLowerCase()
  const matched = q ? templates.filter((t) => t.title.toLowerCase().includes(q)) : templates

  switch (tab) {
    case 'recent': {
      // The gateway already sorts by `updated_at` desc, but a later page or a
      // local rename must not be able to reorder the row, so it is sorted
      // here too rather than trusted.
      const sorted = [...matched].sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
      // "Zuletzt" is a shortlist, but a *search* is not: slicing a query's
      // matches to eight hides forms the person can see the name of and says
      // nothing about it. While something is typed, every match is shown.
      return q ? sorted : sorted.slice(0, RECENT_LIMIT)
    }
    case 'favorites':
      return matched.filter((t) => favorites.has(t.id))
    default:
      return matched
  }
}

// -- The card's status line ------------------------------------------------

export type CardStateKind =
  | 'loading'
  | 'unconfigured'
  | 'empty'
  | 'unclear'
  | 'inProgress'
  | 'done'

export interface CardState {
  kind: CardStateKind
  /** The line under the name. Never says more than the counts support. */
  label: string
  icon: string
  /** Tailwind colour class from the Fx ramp. */
  tone: string
  /** The green check badge over the thumbnail. */
  done: boolean
}

/** A template with no configured box cannot read a document yet. */
export function templateHasFields(template: Template): boolean {
  return (
    (template.fieldCount ?? 0) > 0 ||
    template.pageList.some((page) => (page.boxConfiguration?.length ?? 0) > 0)
  )
}

/**
 * The card's status from its counts. `counts` undefined means the per-card
 * request has not answered yet — a card that guesses "analysiert" and then
 * corrects itself to "unklar" is worse than one that says nothing for a
 * moment.
 */
export function cardState(template: Template, counts: DocumentCounts | undefined): CardState {
  if (!templateHasFields(template)) {
    return {
      kind: 'unconfigured',
      label: 'Felder noch nicht festgelegt',
      icon: 'build',
      tone: 'text-text-muted',
      done: false,
    }
  }
  if (!counts) {
    return { kind: 'loading', label: '', icon: '', tone: 'text-text-muted', done: false }
  }

  const unclear = counts.needsReview ?? 0
  if (unclear > 0) {
    return {
      kind: 'unclear',
      label: `${unclear} unklar`,
      icon: 'error',
      tone: 'text-danger-text',
      done: false,
    }
  }

  const inProgress = counts.byStatus?.inProgress ?? 0
  if (inProgress > 0) {
    return {
      kind: 'inProgress',
      label: `${inProgress} in Verarbeitung`,
      icon: 'schedule',
      tone: 'text-warning-text',
      done: false,
    }
  }

  const analysed = counts.byStatus?.complete ?? 0
  if (analysed > 0) {
    return {
      kind: 'done',
      label: `${analysed} analysiert`,
      icon: 'check_circle',
      tone: 'text-success-text',
      done: (counts.total ?? 0) === analysed,
    }
  }

  return {
    kind: 'empty',
    label: counts.total > 0 ? 'noch nicht verarbeitet' : 'noch keine Dokumente',
    icon: counts.total > 0 ? 'refresh' : 'note_add',
    tone: 'text-text-muted',
    done: false,
  }
}

/** Whether a template's counts are worth polling — only while work is running. */
export function isBusy(counts: DocumentCounts | undefined): boolean {
  return (counts?.byStatus?.inProgress ?? 0) > 0
}

// -- The review banner -----------------------------------------------------

export interface ReviewBanner {
  /** Documents waiting, summed over the templates whose counts have loaded. */
  documents: number
  /** How many templates they are spread over. */
  templates: number
  /** Where "Unklarheiten beheben" goes: the first form that has any. */
  firstTemplateId: string
  text: string
}

/**
 * The artboard promises "31 Felder sind unklar in 2 Musterformularen". A
 * field-level count across templates is not obtainable: `GET
 * /v1/templates/:id/documents/count` answers `{total, byStatus, needsReview,
 * byMatchedPage}` — **documents**, not fields — and a field number would mean
 * reading every document of every template. So the banner counts documents
 * and says documents.
 */
export function reviewBanner(
  entries: { id: string; counts: DocumentCounts | undefined }[],
): ReviewBanner | null {
  let documents = 0
  let templates = 0
  let firstTemplateId = ''
  for (const entry of entries) {
    const waiting = entry.counts?.needsReview ?? 0
    if (waiting <= 0) continue
    documents += waiting
    templates += 1
    if (!firstTemplateId) firstTemplateId = entry.id
  }
  if (documents === 0) return null

  const docWord = documents === 1 ? '1 Dokument wartet' : `${documents} Dokumente warten`
  const formWord = templates === 1 ? '1 Musterformular' : `${templates} Musterformularen`
  return {
    documents,
    templates,
    firstTemplateId,
    text: `${docWord} auf Ihre Prüfung – in ${formWord}.`,
  }
}

// -- Dates -----------------------------------------------------------------

const dateFormat = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

/**
 * "heute", "gestern", "vor 4 Tagen", then the date.
 *
 * `updatedAt` on a template is **microseconds** since the epoch — the
 * worker's vocabulary, mirrored by the gateway's `UpdatedAtMicros` — so it is
 * a thousand times what `Date` expects, and a missing value is left blank
 * rather than rendered as 1970.
 */
export function relativeDate(micros: number | undefined, now: Date = new Date()): string {
  if (!micros) return ''
  const then = new Date(micros / 1000)
  if (Number.isNaN(then.getTime())) return ''

  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const days = Math.round((startOf(now) - startOf(then)) / 86_400_000)

  if (days <= 0) return 'heute'
  if (days === 1) return 'gestern'
  if (days < 7) return `vor ${days} Tagen`
  return dateFormat.format(then)
}
